import { AlertCircle, FileText, Loader2 } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'

const selectTriggerClass =
  'h-7 w-full min-w-0 gap-1 border-[#D1D5D8] bg-transparent px-2 py-0 text-[12px] text-[#2C3D4D] data-[placeholder]:text-[#9CA3AF] focus-visible:ring-[0px]'

export const BulkQuoteModal = ({
  open,
  onOpenChange,
  selectedEstimates,
  onConfirm,
  isSubmitting,
  error,
  clients = [],
  insuranceCompanies = [],
  defaults = {},
  isPreparing = false
}) => {
  // 1 estimate ↔ 1 quote: skip anything already linked to a QWI on top of the
  // existing no-findings skip rule.
  const alreadyLinked = selectedEstimates.filter((e) => e.record?.href)
  const noFindings = selectedEstimates.filter((e) => !e.record?.href && !(e.calibrationsCount != null && e.calibrationsCount > 0))
  const quotable = selectedEstimates.filter((e) => !e.record?.href && e.calibrationsCount != null && e.calibrationsCount > 0)
  const count = quotable.length

  // Per-row client / insurance choice. Seeded from what the backend resolved for each estimate,
  // but always editable and always required: an estimate whose shop has no client record resolves
  // to nothing, and submitting it blind is what the API rejects with "client_id is required".
  const [assignments, setAssignments] = useState({})

  useEffect(() => {
    if (!open) {
      setAssignments({})
      return
    }
    // Defaults land asynchronously (the detail fetch) — merge them in without discarding a
    // choice the user has already made while waiting.
    setAssignments((prev) => {
      const next = { ...prev }
      Object.entries(defaults).forEach(([id, resolved]) => {
        next[id] = {
          clientId: prev[id]?.clientId ?? resolved?.clientId ?? null,
          insuranceCompanyId: prev[id]?.insuranceCompanyId ?? resolved?.insuranceCompanyId ?? null
        }
      })
      return next
    })
  }, [open, defaults])

  const rowOf = (id) => assignments[id] || {}
  const setRow = (id, patch) => setAssignments((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }))

  const incomplete = quotable.filter((e) => !rowOf(e.id).clientId || !rowOf(e.id).insuranceCompanyId)
  const canCreate = count > 0 && incomplete.length === 0 && !isSubmitting && !isPreparing

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[640px] p-0 rounded-2xl overflow-hidden'>
        <DialogHeader className='border-b border-[#D1D5D8] px-6 py-5 space-y-1'>
          <DialogTitle className='text-lg font-semibold text-[#0A1628]'>
            Create {count} {count === 1 ? 'Quote' : 'Quotes'}
          </DialogTitle>
          <DialogDescription className='text-[13px] text-[#6B7280]'>
            Quotes will be created using auto-pricing. Confirm the client and insurance on each row, then review each quote before sending.
          </DialogDescription>
        </DialogHeader>

        <div className='max-h-96 overflow-auto px-6 py-2'>
          {isPreparing ? (
            <div className='flex items-center justify-center gap-2 py-8 text-sm text-[#6B7280]'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading estimate details…
            </div>
          ) : quotable.length === 0 ? (
            <div className='py-6 text-center text-sm text-[#6B7280]'>No quotable estimates in the selection.</div>
          ) : (
            quotable.map((e, i) => (
              <div key={e.id || e.ro} className='py-2.5' style={{ borderBottom: i < quotable.length - 1 ? '1px solid #E8ECF1' : 'none' }}>
                <div className='flex items-center gap-2 truncate text-[13px]'>
                  <span className='font-semibold text-[#0A1628]'>{e.ro || '—'}</span>
                  <span className='text-[#6B7280]'>·</span>
                  <span className='truncate text-[#0A1628]'>{e.vehicle}</span>
                  <span className='text-[#6B7280]'>·</span>
                  <span className='text-[#1F6FEB]'>
                    {e.calibrationsCount} {e.calibrationsCount === 1 ? 'cal' : 'cals'}
                  </span>
                </div>
                <div className='mt-1.5 grid grid-cols-2 gap-2'>
                  <Select
                    value={rowOf(e.id).clientId ? String(rowOf(e.id).clientId) : ''}
                    onValueChange={(v) => setRow(e.id, { clientId: Number(v) })}
                  >
                    <SelectTrigger className={selectTriggerClass} aria-label={`Client for ${e.ro || 'estimate'}`}>
                      {/* Placeholder carries the estimate's own extracted name, same as the detail
                          page, so an unresolved row still shows what the document said. */}
                      <SelectValue placeholder={e.client && e.client !== '—' ? e.client : 'Select client'} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name || c.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={rowOf(e.id).insuranceCompanyId ? String(rowOf(e.id).insuranceCompanyId) : ''}
                    onValueChange={(v) => setRow(e.id, { insuranceCompanyId: Number(v) })}
                  >
                    <SelectTrigger className={selectTriggerClass} aria-label={`Insurance for ${e.ro || 'estimate'}`}>
                      <SelectValue placeholder={e.insurance && e.insurance !== '—' ? e.insurance : 'Select insurance'} />
                    </SelectTrigger>
                    <SelectContent>
                      {insuranceCompanies.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name || c.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))
          )}
        </div>

        {(noFindings.length > 0 || alreadyLinked.length > 0) && (
          <div className='border-t border-[#E8ECF1] bg-[#F8FAFC] px-6 py-3 text-xs text-[#6B7280] space-y-1'>
            {alreadyLinked.length > 0 && (
              <div>
                {alreadyLinked.length} {alreadyLinked.length === 1 ? 'estimate' : 'estimates'} skipped — already linked to a quote.
              </div>
            )}
            {noFindings.length > 0 && (
              <div>
                {noFindings.length} {noFindings.length === 1 ? 'estimate' : 'estimates'} skipped — no findings.
              </div>
            )}
          </div>
        )}

        {!isPreparing && incomplete.length > 0 && (
          <div className='flex items-center gap-2 border-t border-[#FDE68A] bg-[#FFFBEB] px-6 py-3 text-xs text-[#92400E]'>
            <AlertCircle className='h-4 w-4 flex-shrink-0' />
            <span>
              {incomplete.length} {incomplete.length === 1 ? 'estimate needs' : 'estimates need'} a client and insurance company before
              {incomplete.length === 1 ? ' it' : ' they'} can be quoted.
            </span>
          </div>
        )}

        {error && (
          <div className='flex items-center gap-2 border-t border-[#FCA5A5] bg-[#FEF2F2] px-6 py-3 text-xs text-[#B91C1C]'>
            <AlertCircle className='h-4 w-4 flex-shrink-0' />
            <span className='truncate'>{error}</span>
          </div>
        )}

        <DialogFooter className='flex items-center justify-between border-t border-[#D1D5D8] px-6 py-4 sm:justify-between'>
          <button type='button' onClick={() => onOpenChange(false)} className='text-[13px] font-medium text-[#6B7280] hover:underline'>
            Cancel
          </button>
          <button
            type='button'
            onClick={() => onConfirm(quotable, assignments)}
            disabled={!canCreate}
            className='inline-flex h-9 items-center gap-2 rounded-lg bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-60'
          >
            {isSubmitting ? <Loader2 className='h-4 w-4 animate-spin' /> : <FileText className='h-4 w-4' />}
            Create {count} {count === 1 ? 'Quote' : 'Quotes'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

BulkQuoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  selectedEstimates: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  error: PropTypes.string,
  clients: PropTypes.array,
  insuranceCompanies: PropTypes.array,
  /** estimateId → { clientId, insuranceCompanyId } resolved by the backend; seeds each row. */
  defaults: PropTypes.object,
  isPreparing: PropTypes.bool
}
